import React, { useState, useEffect } from 'react'
import { userService } from '../services/api'

export default function UserManagement() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'Viewer'
  })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const roles = ['Super Admin', 'Admin', 'Site Leader', 'Management', 'Viewer']

  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await userService.getAllUsers()
      setUsers(response.data.data || [])
    } catch (error) {
      console.error('Failed to load users:', error)
      setError('Failed to load users. You may not have permission.')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUser = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    // Basic validation
    if (!formData.email || !formData.password || !formData.role) {
      setError('All fields are required')
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    try {
      await userService.createUser(formData)
      setSuccess('User created successfully!')
      setFormData({ email: '', password: '', role: 'Viewer' })
      setShowCreateForm(false)
      loadUsers()
    } catch (error) {
      console.error('Failed to create user:', error)
      setError(error.response?.data?.message || 'Failed to create user')
    }
  }

  const handleEditUser = (user) => {
    setEditingUser(user)
    setFormData({
      email: user.email,
      password: '',
      role: user.role
    })
    setShowCreateForm(true)
  }

  const handleUpdateUser = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (!editingUser) return

    // Basic validation
    if (!formData.email || !formData.role) {
      setError('Email and role are required')
      return
    }

    // Only validate password if provided
    if (formData.password && formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    try {
      const updateData = {
        email: formData.email,
        role: formData.role
      }

      // Only include password if it's provided
      if (formData.password) {
        updateData.password = formData.password
      }

      await userService.updateUser(editingUser.user_id, updateData)
      setSuccess('User updated successfully!')
      setEditingUser(null)
      setFormData({ email: '', password: '', role: 'Viewer' })
      setShowCreateForm(false)
      loadUsers()
    } catch (error) {
      console.error('Failed to update user:', error)
      setError(error.response?.data?.message || 'Failed to update user')
    }
  }

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return
    }

    try {
      await userService.deleteUser(userId)
      setSuccess('User deleted successfully!')
      loadUsers()
    } catch (error) {
      console.error('Failed to delete user:', error)
      setError(error.response?.data?.message || 'Failed to delete user')
    }
  }

  const handleCancelEdit = () => {
    setEditingUser(null)
    setFormData({ email: '', password: '', role: 'Viewer' })
    setShowCreateForm(false)
  }

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Super Admin':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
      case 'Admin':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
      case 'Site Leader':
        return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
      case 'Management':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300'
    }
  }

  const getStatusBadgeColor = (status) => {
    return status === 'Active'
      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
      : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-slate-900 border-r-transparent dark:border-sky-400 dark:border-r-transparent"></div>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">Loading users...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">User Management</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
            Create and manage user accounts. Only Super Admin can access this feature.
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="inline-flex items-center justify-center rounded-xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 dark:border-sky-400 dark:bg-sky-500 dark:text-white dark:hover:bg-sky-400"
        >
          {showCreateForm ? 'Cancel' : 'Create New User'}
        </button>
      </div>

      {error && (
        <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/20">
          <p className="text-sm font-medium text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {success && (
        <div className="rounded-xl border-2 border-green-200 bg-green-50 p-4 dark:border-green-900/40 dark:bg-green-900/20">
          <p className="text-sm font-medium text-green-800 dark:text-green-300">{success}</p>
        </div>
      )}

      {showCreateForm && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
            {editingUser ? 'Edit User' : 'Create New User'}
          </h3>
          <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-slate-900 dark:text-slate-100">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                placeholder="user@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-slate-900 dark:text-slate-100">
                Password {editingUser && '(leave empty to keep current)'}
              </label>
              <input
                type="password"
                id="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                placeholder={editingUser ? '••••••••' : '••••••••'}
                required={!editingUser}
              />
            </div>

            <div>
              <label htmlFor="role" className="mb-1 block text-sm font-medium text-slate-900 dark:text-slate-100">
                Role
              </label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                required
              >
                {roles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-xl border-2 border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 dark:border-sky-400 dark:bg-sky-500 dark:text-white dark:hover:bg-sky-400"
              >
                {editingUser ? 'Update User' : 'Create User'}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th className="px-6 py-3 font-semibold text-slate-900 dark:text-slate-100">Email</th>
              <th className="px-6 py-3 font-semibold text-slate-900 dark:text-slate-100">Role</th>
              <th className="px-6 py-3 font-semibold text-slate-900 dark:text-slate-100">Status</th>
              <th className="px-6 py-3 font-semibold text-slate-900 dark:text-slate-100">Last Login</th>
              <th className="px-6 py-3 font-semibold text-slate-900 dark:text-slate-100">Created</th>
              <th className="px-6 py-3 font-semibold text-slate-900 dark:text-slate-100">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {users.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">
                  No users found. Create your first user to get started.
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.user_id} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                  <td className="px-6 py-4 text-slate-900 dark:text-slate-100">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getRoleBadgeColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeColor(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-sm font-medium text-sky-600 transition hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.user_id)}
                        className="text-sm font-medium text-red-600 transition hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
